import { Link, NavLink, useLocation, useResolvedPath } from 'react-router';
import type { LinkProps, NavLinkProps, To } from 'react-router';
import type { RefAttributes } from 'react';

/**
 * Should following `to` play a View Transition? Yes whenever it leads to another *page* — the old
 * page cross-fades into the new one, and the product card's image and title travel to the product
 * page (DESIGN_SYSTEM §4.6). Staying on the same page (a hash jump, a different query string) does
 * not: filters and in-page anchors must feel instant. Browsers without the API, and visitors who
 * prefer reduced motion, simply navigate (the CSS zeroes the animation for them).
 */
function useTransitionDefault(to: To, explicit: boolean | undefined): boolean {
  const { pathname } = useLocation();
  const target = useResolvedPath(to);
  return explicit ?? target.pathname !== pathname;
}

/** Router `Link` that plays the page transition for navigations between pages. */
export function AppLink({
  to,
  viewTransition,
  ...props
}: LinkProps & RefAttributes<HTMLAnchorElement>) {
  return <Link to={to} viewTransition={useTransitionDefault(to, viewTransition)} {...props} />;
}

/** Router `NavLink` (adds `aria-current="page"` when active) with the same transition rules. */
export function AppNavLink({
  to,
  viewTransition,
  end,
  ...props
}: NavLinkProps & RefAttributes<HTMLAnchorElement>) {
  return (
    <NavLink
      to={to}
      {...(end === undefined ? {} : { end })}
      viewTransition={useTransitionDefault(to, viewTransition)}
      {...props}
    />
  );
}
